import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Settings, Plus, ToggleLeft, ToggleRight, ArrowRight } from 'lucide-react';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

const SERVICE_ICONS = {
  sound_box: '🔊', upi_qr: '📱', current_savings: '🏦',
  daily_account: '📅', fixed_deposit: '📈', recurring_deposit: '🔁',
  monthly_interest: '💵', loan: '💰', other: '📦'
};

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

export default function OwnerServices() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newService, setNewService] = useState({ name: '', description: '' });
  const [toast, setToast] = useState({ show: false, msg: '' });

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadServices = useCallback(async () => {
    try {
      const res = await api.get('/services/all');
      setServices(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadServices(); }, [loadServices]);

  const handleToggle = async (serviceId, e) => {
    e.stopPropagation(); // Prevent card click
    try {
      await api.patch(`/services/${serviceId}/toggle`);
      setServices(prev => prev.map(s => s.id === serviceId ? { ...s, isActive: !s.isActive } : s));
      showToast('Service toggled');
    } catch {
      showToast('Failed to toggle');
    }
  };

  const handleAddService = async () => {
    if (!newService.name.trim()) return;
    try {
      const res = await api.post('/services', newService);
      setServices(prev => [...prev, res.data]);
      setNewService({ name: '', description: '' });
      setShowAddModal(false);
      showToast('Service added');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/owner/merchants?service=${serviceId}`);
  };

  const totalMerchants = services.reduce((sum, s) => sum + (s.merchantCount || 0), 0);
  const upiService = services.find(s => s.slug === 'upi_qr');

  return (
    <OwnerLayout title="Services">
      {/* UPI/QR Highlight - Primary Product */}
      {upiService && (
        <div 
          className="sb-panel mb-4 fu clickable" 
          data-testid="upi-highlight"
          onClick={() => navigate(`/owner/merchants?service=${upiService.id}`)}
        >
          <div className="sb-panel-header">
            <div className="sb-panel-icon"><Smartphone size={16} color="white" /></div>
            <div>
              <div className="sb-panel-title">UPI/QR Service — Primary Product</div>
              <div className="sb-panel-sub">Auto-includes Current/Savings Account</div>
            </div>
          </div>
          <div className="sb-stats-grid">
            <div className="sb-stat">
              <div className="sl">Total Leads</div>
              <div className="sv big">{upiService.merchantCount || 0}</div>
            </div>
            <div className="sb-stat">
              <div className="sl">Status</div>
              <div className="sv">{upiService.isActive ? 'Active' : 'Inactive'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">All Services</h2>
          <p className="text-sm text-gray">{services.length} services · {totalMerchants} total interest</p>
        </div>
        <button className="btn-navy" onClick={() => setShowAddModal(true)} data-testid="add-service-btn">
          <Plus size={14} style={{ marginRight: '4px' }} /> Add Service
        </button>
      </div>

      {/* Services Grid */}
      {loading ? (
        <div className="p-8 text-center text-gray">Loading…</div>
      ) : (
        <div className="services-grid">
          {services.map(svc => {
            const isPrimary = svc.slug === 'upi_qr';
            const isAutoIncluded = svc.slug === 'current_savings';
            return (
              <div
                key={svc.id}
                className={`service-card clickable ${isPrimary ? 'upi-primary' : ''}`}
                data-testid={`service-${svc.slug}`}
                onClick={() => handleServiceClick(svc.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: '20px' }}>{SERVICE_ICONS[svc.slug] || '📋'}</span>
                    <div>
                      <div className="font-heading font-bold text-navy">{svc.name}</div>
                      {isPrimary && (
                        <span className="text-xs text-red font-semibold">PRIMARY</span>
                      )}
                      {isAutoIncluded && (
                        <span className="text-xs text-green-600 font-semibold">AUTO-INCLUDED</span>
                      )}
                    </div>
                  </div>
                  <button
                    className={`p-1 ${svc.isActive ? 'text-green-600' : 'text-gray-400'}`}
                    onClick={(e) => handleToggle(svc.id, e)}
                    title={svc.isActive ? 'Click to deactivate' : 'Click to activate'}
                    data-testid={`toggle-${svc.slug}`}
                  >
                    {svc.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                </div>
                <div className="text-sm text-gray mb-2">{svc.description || 'No description'}</div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray">Merchants interested</div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-bold text-navy">{svc.merchantCount || 0}</span>
                    <ArrowRight size={14} className="text-gray" />
                  </div>
                </div>
                {isAutoIncluded && (
                  <div className="text-xs text-gray mt-2" style={{ background: 'var(--g1)', padding: '4px 8px', borderRadius: '4px' }}>
                    Auto-added with UPI/QR
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Service Modal */}
      {showAddModal && (
        <div className="owner-modal-overlay open" onClick={e => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div className="owner-modal" style={{ width: '420px' }}>
            <div className="modal-head">
              <div className="modal-icon"><Plus size={16} /></div>
              <div>
                <div className="modal-title">Add New Service</div>
                <div className="modal-subtitle">Create a new service offering</div>
              </div>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Service Name</label>
                <input
                  className="form-input"
                  placeholder="e.g. Gold Loan"
                  value={newService.name}
                  onChange={e => setNewService(p => ({ ...p, name: e.target.value }))}
                  data-testid="service-name-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Brief description of the service"
                  value={newService.description}
                  onChange={e => setNewService(p => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="btn-navy" onClick={handleAddService} data-testid="save-service-btn">Add Service</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </OwnerLayout>
  );
}
